import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft, ChevronRight, Loader2, MapPin, ExternalLink, CalendarClock,
  Link2, Eye, ShieldCheck, Calendar as CalIcon, X, Check, ArrowUpRight,
  CalendarX, Clock, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { openOAuthTab } from "@/lib/oauth";
import { demoEvents } from "@/lib/msDemoData";
import { outlookCalendarCompose, openUrl } from "@/lib/outlook";
import { BRAND, brandRgba } from "@/lib/brand";
import { toast } from "sonner";

const DEMO_KEY = "ph_ms_demo";
const isDemoOn = () => { try { return localStorage.getItem(DEMO_KEY) === "1"; } catch { return false; } };

const VIEWS = [
  { key: "agenda", label: "Agenda" },
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

/* ---------- date helpers ---------- */
const parseDt = (iso) => { if (!iso) return null; const s = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z"; const d = new Date(s); return isNaN(d) ? null : d; };
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const fmtT = (d) => d ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
const fmtDayLong = (d) => d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function eventColor(ev) {
  // Stable hue from subject
  let h = 0;
  for (const c of (ev.subject || "")) h = (h * 31 + c.charCodeAt(0)) % 360;
  return `hsl(${h}, 55%, 55%)`;
}

export default function MeetingsCalendar() {
  const [view, setView] = useState("agenda");
  const [anchor, setAnchor] = useState(() => new Date());
  const [events, setEvents] = useState(() => (isDemoOn() ? demoEvents() : []));
  const [pending, setPending] = useState([]);
  const [status, setStatus] = useState(() => (isDemoOn() ? { connected: false, configured: true } : null));
  const [loading, setLoading] = useState(() => !isDemoOn());
  const [connecting, setConnecting] = useState(false);
  const [demo, setDemo] = useState(isDemoOn);
  const [activeEvent, setActiveEvent] = useState(null);

  // Compute the fetch window for the current view/anchor.
  const window = useMemo(() => {
    if (view === "day") return { start: startOfDay(anchor), days: 1 };
    if (view === "week") return { start: startOfWeek(anchor), days: 7 };
    if (view === "month") return { start: startOfWeek(startOfMonth(anchor)), days: 42 };
    return { start: startOfDay(anchor), days: 7 }; // agenda
  }, [view, anchor]);

  const loadPending = useCallback(async () => {
    try {
      const r = await api.get("/calendar/pending");
      setPending(r.data || []);
    } catch { /* ignore */ }
  }, []);

  // Status + pending on mount.
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/calendar/pending"); setPending(r.data || []); } catch { /* ignore */ }
    })();
    if (isDemoOn()) return;
    (async () => {
      try {
        const r = await api.get("/integrations/microsoft/status");
        setStatus(r.data);
        if (!r.data.connected) setLoading(false);
      } catch { setLoading(false); }
    })();
  }, []);

  // Reload events when window changes (deferred via timer so setState isn't synchronous in the effect).
  const winStartIso = window.start.toISOString();
  const winDays = window.days;
  useEffect(() => {
    const id = setTimeout(async () => {
      if (demo) { setEvents(demoEvents()); setLoading(false); return; }
      if (!status?.connected) return;
      setLoading(true);
      try {
        const r = await api.get(`/integrations/microsoft/events?start=${winStartIso}&days=${winDays}`);
        setEvents(r.data.events || []);
      } catch {
        toast.error("Couldn't read your calendar from Microsoft 365.");
      } finally {
        setLoading(false);
      }
    }, 0);
    return () => clearTimeout(id);
  }, [winStartIso, winDays, status, demo]);

  const move = (dir) => {
    if (view === "month") setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + dir, 1));
    else if (view === "week") setAnchor((a) => addDays(a, dir * 7));
    else if (view === "day") setAnchor((a) => addDays(a, dir));
    else setAnchor((a) => addDays(a, dir * 7)); // agenda moves a week at a time
  };

  const enableDemo = () => {
    try { localStorage.setItem(DEMO_KEY, "1"); } catch { /* ignore */ }
    setDemo(true); setStatus({ connected: false, configured: true }); setEvents(demoEvents()); setLoading(false);
    toast.message("Showing sample calendar — not a live Microsoft connection.");
  };
  const exitDemo = () => {
    try { localStorage.removeItem(DEMO_KEY); } catch { /* ignore */ }
    setDemo(false); setLoading(true); setEvents([]);
    api.get("/integrations/microsoft/status").then((r) => { setStatus(r.data); if (!r.data.connected) setLoading(false); }).catch(() => setLoading(false));
  };
  const connect = async () => {
    setConnecting(true);
    const tab = openOAuthTab();
    try { const r = await api.get("/integrations/microsoft/connect"); tab.go(r.data.authorize_url); }
    catch (e) { tab.fail(); toast.error(e?.response?.data?.detail || "Microsoft 365 isn't configured yet."); setConnecting(false); }
  };

  const periodLabel = useMemo(() => {
    if (view === "month") return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    if (view === "day") return fmtDayLong(anchor);
    const s = view === "week" ? startOfWeek(anchor) : startOfDay(anchor);
    const e = addDays(s, 6);
    const opts = { month: "short", day: "numeric" };
    return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, { ...opts, year: "numeric" })}`;
  }, [view, anchor]);

  // group events by local day
  const byDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const d = parseDt(ev.start); if (!d) continue;
      const k = dayKey(d);
      (map[k] = map[k] || []).push(ev);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => (parseDt(a.start) || 0) - (parseDt(b.start) || 0)));
    return map;
  }, [events]);

  const showCalendar = status?.connected || demo;

  return (
    <div data-testid="meetings-calendar">
      <PendingStrip pending={pending} onChanged={loadPending} />

      {/* connect / demo states */}
      {status && !status.connected && !demo && (
        <div className="inbox-connect-card" data-testid="calendar-connect" style={{ marginTop: 4 }}>
          <CalIcon size={26} className="text-cobalt" />
          <h3 style={{ margin: "12px 0 6px" }}>Connect your calendar</h3>
          <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 460 }}>
            Link Microsoft 365 to see your day, week and month at a glance — read-only.
          </div>
          <div className="inbox-scopes"><span><CalIcon size={13} /> Read calendar</span><span><ShieldCheck size={13} /> Drafts only</span></div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap", justifyContent: "center" }}>
            <button className="btn btn-primary" onClick={connect} disabled={connecting} data-testid="calendar-connect-btn">
              {connecting ? <Loader2 size={15} className="spin" /> : <Link2 size={15} />}{connecting ? "Redirecting…" : "Connect Microsoft 365"}
            </button>
            <button className="btn" onClick={enableDemo} data-testid="calendar-demo-btn"><Eye size={15} /> Preview sample</button>
          </div>
        </div>
      )}

      {showCalendar && (
        <>
          {/* toolbar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, margin: "6px 0 18px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => move(-1)} data-testid="cal-prev" style={{ padding: 8 }}><ChevronLeft size={16} /></button>
              <button className="btn btn-ghost" onClick={() => setAnchor(new Date())} data-testid="cal-today" style={{ padding: "8px 12px", fontSize: 13 }}>Today</button>
              <button className="btn btn-ghost" onClick={() => move(1)} data-testid="cal-next" style={{ padding: 8 }}><ChevronRight size={16} /></button>
              <div data-testid="cal-period-label" style={{ fontSize: 15, color: "var(--ink)", fontWeight: 600, marginLeft: 6 }}>{periodLabel}</div>
            </div>
            <div className="seg" data-testid="calendar-view-switcher">
              {VIEWS.map((v) => (
                <button key={v.key} className={view === v.key ? "active" : ""} onClick={() => setView(v.key)} data-testid={`cal-view-${v.key}`}>{v.label}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-dim" style={{ padding: 40, textAlign: "center" }}>
              <Loader2 size={18} className="spin" style={{ marginBottom: 8 }} /><div>Loading calendar…</div>
            </div>
          ) : (
            <>
              {view === "agenda" && <AgendaView start={window.start} byDay={byDay} onOpen={setActiveEvent} />}
              {view === "day" && <DayView day={anchor} events={byDay[dayKey(startOfDay(anchor))] || []} onOpen={setActiveEvent} />}
              {view === "week" && <WeekView start={startOfWeek(anchor)} byDay={byDay} onOpen={setActiveEvent} />}
              {view === "month" && <MonthView anchor={anchor} byDay={byDay} onOpen={setActiveEvent} />}
            </>
          )}

          {demo && (
            <div style={{ marginTop: 18 }}>
              <button className="btn btn-ghost" onClick={exitDemo} style={{ fontSize: 12 }}><X size={13} /> Exit sample preview</button>
            </div>
          )}
        </>
      )}

      {activeEvent && (
        <EventDetailModal ev={activeEvent} demo={demo} onClose={() => setActiveEvent(null)} />
      )}
    </div>
  );
}

/* ---------- pending AI invites ---------- */
function PendingStrip({ pending, onChanged }) {
  if (!pending || pending.length === 0) return null;
  const openOutlook = (inv) => {
    openUrl(outlookCalendarCompose({
      subject: inv.title,
      start: inv.start || undefined,
      end: inv.end || undefined,
      location: inv.location || undefined,
      attendees: (inv.attendees || []).join(";") || undefined,
      body: inv.notes || (inv.when_text ? `Proposed time: ${inv.when_text}` : ""),
    }));
    toast.message("Opened Outlook — review and send the invite there.");
  };
  const setStatusInv = async (inv, st) => {
    try { await api.patch(`/calendar/pending/${inv.id}`, { status: st }); onChanged && onChanged(); }
    catch { toast.error("Couldn't update."); }
  };
  return (
    <div data-testid="pending-invites" style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ph-accent-purple)", fontWeight: 600, marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>
        <CalendarClock size={13} /> Pending invites — approve in Outlook
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {pending.map((inv) => {
          const sd = parseDt(inv.start);
          const when = sd ? `${sd.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${fmtT(sd)}` : (inv.when_text || "Time TBD");
          return (
            <div key={inv.id} data-testid={`pending-invite-${inv.id}`} className="fade-up" style={{
              display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12,
              background: brandRgba(BRAND.accentPurple, 0.06), border: `1px solid ${brandRgba(BRAND.accentPurple, 0.32)}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: "var(--ink)" }}>{inv.title}</div>
                <div className="text-dim" style={{ fontSize: 12, marginTop: 3 }}>
                  {when}
                  {inv.attendees?.length ? ` · ${inv.attendees.join(", ")}` : ""}
                  {inv.location ? ` · ${inv.location}` : ""}
                </div>
              </div>
              <span style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ph-accent-purple)", padding: "2px 8px", borderRadius: 999, background: brandRgba(BRAND.accentPurple, 0.12), border: `1px solid ${brandRgba(BRAND.accentPurple, 0.4)}`, flexShrink: 0 }}>Pending</span>
              <button className="btn btn-primary" onClick={() => openOutlook(inv)} data-testid={`pending-open-outlook-${inv.id}`} style={{ padding: "8px 14px", flexShrink: 0 }}>
                <ArrowUpRight size={14} /> Open in Outlook
              </button>
              <button className="btn btn-ghost" onClick={() => setStatusInv(inv, "dismissed")} data-testid={`pending-dismiss-${inv.id}`} title="Dismiss" style={{ padding: 8, flexShrink: 0 }}>
                <X size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- event row / chip ---------- */
function EventRow({ ev, onOpen }) {
  const sd = parseDt(ev.start);
  return (
    <button type="button" onClick={() => onOpen && onOpen(ev)} data-testid={`cal-event-${ev.id}`}
      style={{ display: "flex", gap: 12, padding: "11px 14px", borderRadius: 10, background: "var(--panel)", border: "1px solid var(--hairline)", borderLeft: `3px solid ${eventColor(ev)}`, textDecoration: "none", alignItems: "flex-start", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
      <div style={{ width: 64, flexShrink: 0, fontSize: 12, color: "var(--ink-soft)", fontWeight: 600, paddingTop: 1 }}>
        {ev.is_all_day ? "All day" : fmtT(sd)}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.subject}</div>
        <div className="text-dim" style={{ fontSize: 11.5, marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {ev.location && <span><MapPin size={11} style={{ verticalAlign: -1 }} /> {ev.location}</span>}
          {ev.organizer && <span>{ev.organizer}</span>}
        </div>
      </div>
      <ExternalLink size={13} style={{ color: "var(--ink-dim)", flexShrink: 0, marginTop: 2 }} />
    </button>
  );
}

function EmptyDay({ label }) {
  return <div className="text-dim" style={{ fontSize: 13, padding: "16px 4px" }}>{label}</div>;
}


function AgendaView({ start, byDay, onOpen }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const today = new Date();
  const hasAny = days.some((d) => (byDay[dayKey(d)] || []).length);
  if (!hasAny) return <EmptyDay label="Nothing on the calendar for these 7 days." />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }} data-testid="agenda-view">
      {days.map((d) => {
        const evs = byDay[dayKey(d)] || [];
        if (!evs.length) return null;
        return (
          <div key={dayKey(d)}>
            <div style={{ fontSize: 12, letterSpacing: "0.04em", color: sameDay(d, today) ? "var(--ph-product-teal)" : "var(--ink-soft)", fontWeight: 600, marginBottom: 8 }}>
              {sameDay(d, today) ? "Today · " : ""}{fmtDayLong(d)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {evs.map((ev) => <EventRow key={ev.id} ev={ev} onOpen={onOpen} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ day, events, onOpen }) {
  return (
    <div data-testid="day-view">
      {events.length === 0 ? <EmptyDay label="Nothing scheduled this day." />
        : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{events.map((ev) => <EventRow key={ev.id} ev={ev} onOpen={onOpen} />)}</div>}
    </div>
  );
}

function WeekView({ start, byDay, onOpen }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const today = new Date();
  return (
    <div data-testid="week-view" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(130px, 1fr))", gap: 8, overflowX: "auto" }}>
      {days.map((d) => {
        const evs = byDay[dayKey(d)] || [];
        const isToday = sameDay(d, today);
        return (
          <div key={dayKey(d)} style={{ minWidth: 0, background: "var(--panel)", border: `1px solid ${isToday ? "var(--ph-product-teal)" : "var(--hairline)"}`, borderRadius: 10, padding: 8, minHeight: 160 }}>
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <div className="text-dim" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>{WEEKDAYS[d.getDay()]}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: isToday ? "var(--ph-product-teal)" : "var(--ink)" }}>{d.getDate()}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {evs.map((ev) => <MiniChip key={ev.id} ev={ev} onOpen={onOpen} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, byDay, onOpen }) {
  const gridStart = startOfWeek(startOfMonth(anchor));
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = new Date();
  const month = anchor.getMonth();
  return (
    <div data-testid="month-view">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
        {WEEKDAYS.map((w) => <div key={w} className="text-dim" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", textAlign: "center" }}>{w}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {days.map((d) => {
          const evs = byDay[dayKey(d)] || [];
          const isToday = sameDay(d, today);
          const inMonth = d.getMonth() === month;
          return (
            <div key={dayKey(d)} style={{
              minHeight: 96, background: inMonth ? "var(--panel)" : "var(--panel-2)", border: `1px solid ${isToday ? "var(--ph-product-teal)" : "var(--hairline)"}`,
              borderRadius: 8, padding: 6, opacity: inMonth ? 1 : 0.5, overflow: "hidden",
            }}>
              <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? "var(--ph-product-teal)" : "var(--ink-soft)", marginBottom: 4 }}>{d.getDate()}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {evs.slice(0, 3).map((ev) => <MiniChip key={ev.id} ev={ev} compact onOpen={onOpen} />)}
                {evs.length > 3 && <div className="text-dim" style={{ fontSize: 10 }}>+{evs.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniChip({ ev, compact, onOpen }) {
  const sd = parseDt(ev.start);
  return (
    <button type="button" onClick={() => onOpen && onOpen(ev)} data-testid={`cal-event-${ev.id}`} title={`${ev.is_all_day ? "" : fmtT(sd) + " · "}${ev.subject}`}
      style={{
        display: "block", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit", border: "none",
        fontSize: compact ? 10.5 : 11.5, padding: compact ? "2px 5px" : "4px 7px", borderRadius: 6,
        background: brandRgba(BRAND.accentPurple, 0.10), borderLeft: `2px solid ${eventColor(ev)}`, color: "var(--ink-soft)",
        textDecoration: "none", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
      {!ev.is_all_day && <span style={{ color: "var(--ink-dim)", marginRight: 4 }}>{fmtT(sd)}</span>}{ev.subject}
    </button>
  );
}

/* ---------- event detail + reschedule/decline draft ---------- */
function EventDetailModal({ ev, demo, onClose }) {
  const [mode, setMode] = useState(null); // null | "decline" | "reschedule" | "prep"
  const [note, setNote] = useState("");
  const [proposed, setProposed] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // { web_link, action }
  const [prepLoading, setPrepLoading] = useState(false);
  const [prep, setPrep] = useState(null); // { title, summary, markdown, citations }
  const [prepSaving, setPrepSaving] = useState(false);
  const sd = parseDt(ev.start);
  const ed = parseDt(ev.end);
  const when = sd ? `${fmtDayLong(sd)} · ${fmtT(sd)}${ed ? "–" + fmtT(ed) : ""}` : (ev.is_all_day ? "All day" : "");

  const openOutlook = () => {
    if (ev.web_link && ev.web_link !== "#") openUrl(ev.web_link);
    else toast.message("This is sample data — connect a real calendar to open it in Outlook.");
  };

  const runPrep = async () => {
    if (prepLoading) return;
    if (demo) {
      toast.message("Connect Microsoft 365 to build a real Prep Pack.");
      return;
    }
    setPrepLoading(true);
    setMode("prep");
    try {
      const r = await api.post("/prep-pack", { event_id: ev.id });
      setPrep(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the prep pack.");
      setMode(null);
    } finally {
      setPrepLoading(false);
    }
  };

  const savePrepToVault = async () => {
    if (!prep || prepSaving) return;
    setPrepSaving(true);
    try {
      await api.post("/vault/docs", {
        user_id: "_",
        folder: "00 Inbox",
        name: prep.title || `Prep — ${ev.subject}`,
        markdown: prep.markdown,
        tags: ["prep-pack", "meeting"],
      });
      toast.success("Saved to Vault → 00 Inbox.");
    } catch {
      toast.error("Couldn't save to the vault.");
    } finally {
      setPrepSaving(false);
    }
  };

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.post(`/calendar/events/${encodeURIComponent(ev.id)}/draft-reply`, {
        action: mode,
        note,
        proposed_time: mode === "reschedule" ? proposed : "",
      });
      setSaved({ web_link: r.data?.web_link || null, action: mode });
      toast.success(mode === "decline"
        ? "Decline reply saved to your Outlook Drafts."
        : "Reschedule reply saved to your Outlook Drafts.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the reply draft to Outlook.");
    } finally {
      setSaving(false);
    }
  };

  const lbl = { display: "block", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-dim)", fontWeight: 600, marginBottom: 6 };

  return (
    <div data-testid="event-detail-modal" onMouseDown={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(12,12,16,0.55)", backdropFilter: "blur(4px)", padding: 24 }}>
      <div onMouseDown={(e) => e.stopPropagation()} className="fade-up"
        style={{ width: "100%", maxWidth: 520, background: "var(--panel)", border: "1px solid var(--hairline)", borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,0.35)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px 0" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ph-accent-purple)", fontWeight: 600 }}>Calendar event</div>
          <button onClick={onClose} data-testid="event-modal-close" style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-dim)" }}><X size={18} /></button>
        </div>
        <div style={{ padding: "10px 22px 22px" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", lineHeight: 1.3 }} data-testid="event-modal-title">{ev.subject}</div>
          <div className="text-dim" style={{ fontSize: 13, marginTop: 8, display: "flex", flexDirection: "column", gap: 5 }}>
            {when && <span><CalendarClock size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{when}</span>}
            {ev.location && <span><MapPin size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{ev.location}</span>}
            {ev.organizer && <span>Organizer: {ev.organizer}</span>}
          </div>

          {saved ? (
            <div data-testid="event-reply-saved" style={{ marginTop: 20, padding: "14px 16px", borderRadius: 12, background: "rgba(78,205,196,0.09)", border: "1px solid rgba(78,205,196,0.3)", display: "flex", alignItems: "center", gap: 12 }}>
              <Check size={16} style={{ color: "var(--ph-product-teal)", flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "var(--ink-soft)", flex: 1 }}>
                {saved.action === "decline" ? "Decline" : "Reschedule"} reply saved to your Outlook Drafts. Review and send it there — nothing was sent.
              </span>
              {saved.web_link && (
                <button className="btn" onClick={() => openUrl(saved.web_link)} data-testid="event-reply-open-outlook" style={{ flexShrink: 0 }}>
                  <ArrowUpRight size={14} /> Open
                </button>
              )}
            </div>
          ) : demo ? (
            <div className="inbox-demo-banner" style={{ margin: "18px 0 0" }} data-testid="event-demo-note">
              <Eye size={15} />
              <span>Sample calendar — connect a real Microsoft 365 account to draft reschedule / decline replies.</span>
            </div>
          ) : !mode ? (
            <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
              <button className="btn btn-primary" onClick={runPrep} data-testid="event-prep-btn">
                <Sparkles size={14} /> Prep brief
              </button>
              <button className="btn" onClick={openOutlook} data-testid="event-open-outlook"><ArrowUpRight size={14} /> Open in Outlook</button>
              <button className="btn" onClick={() => setMode("reschedule")} data-testid="event-reschedule-btn"><Clock size={14} /> Propose new time</button>
              <button className="btn" onClick={() => setMode("decline")} data-testid="event-decline-btn"><CalendarX size={14} /> Decline with note</button>
            </div>
          ) : mode === "prep" ? (
            <div style={{ marginTop: 18 }} data-testid="event-prep-panel">
              {prepLoading && !prep ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--ink-soft)", fontSize: 13.5, padding: "20px 0" }}>
                  <Loader2 size={14} className="spin" />
                  Building your prep pack — pulling attendees, recent emails, past meetings…
                </div>
              ) : prep ? (
                <>
                  {prep.summary && (
                    <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--ink-soft)", marginBottom: 14, padding: "10px 12px", borderLeft: "3px solid var(--ph-product-teal)", background: "var(--panel-2)", borderRadius: 4 }}>
                      {prep.summary}
                    </div>
                  )}
                  <div
                    className="prep-md"
                    data-testid="event-prep-markdown"
                    style={{ fontSize: 13.5, lineHeight: 1.7, color: "var(--ink)", whiteSpace: "pre-wrap", maxHeight: 360, overflowY: "auto", padding: "4px 2px 8px" }}
                  >
                    {prep.markdown}
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14, flexWrap: "wrap" }}>
                    <button className="btn btn-ghost" onClick={() => { setMode(null); setPrep(null); }} data-testid="event-prep-back">Back</button>
                    <button
                      className="btn btn-primary"
                      onClick={savePrepToVault}
                      disabled={prepSaving}
                      data-testid="event-prep-save-vault"
                    >
                      {prepSaving ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save to Vault
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="text-soft" style={{ fontSize: 13, lineHeight: 1.55 }}>
                {mode === "decline"
                  ? "I'll draft a polite decline reply to the organizer and save it to your Outlook Drafts. Nothing sends — you review and send it yourself."
                  : "I'll draft a reschedule reply to the organizer and save it to your Outlook Drafts. Nothing sends — you review and send it yourself."}
              </div>
              {mode === "reschedule" && (
                <div>
                  <label style={lbl}>Proposed new time (optional)</label>
                  <input className="field" value={proposed} onChange={(e) => setProposed(e.target.value)} placeholder="e.g. Thursday afternoon, or Jun 18 at 2pm" data-testid="event-proposed-time" />
                </div>
              )}
              <div>
                <label style={lbl}>Add a note (optional)</label>
                <textarea className="field" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything you'd like included…" data-testid="event-reply-note" />
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="btn btn-ghost" onClick={() => { setMode(null); setNote(""); setProposed(""); }} data-testid="event-reply-cancel">Back</button>
                <button className="btn btn-primary" onClick={submit} disabled={saving} data-testid="event-reply-submit">
                  {saving ? <Loader2 size={14} className="spin" /> : <Check size={14} />} {mode === "decline" ? "Draft decline reply" : "Draft reschedule reply"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
